import React, { ComponentType } from 'react'
import log from 'lib/log'
import { loadProfile } from 'state/actions/session'
import { loadInstanceInfo } from 'state/actions/instance'
import { updatePageHead } from 'state/actions/head'
import { API } from 'lib-core'

export interface DataRequirements {
  instance: boolean
  session: boolean
  admin: boolean
}

const DEFAULT_DATA_REQUIREMENTS: DataRequirements = {
  instance: true,
  session: true
}

interface AsyncPageOptions {
  key?: string
  title?: string
  pageComponent?: ComponentType
  isChromeless?: boolean
  basicDataRequirements?: Partial<DataRequirements>
}

let count = 1

class AsyncPage<P = any>{
  key: string
  title: string = ''
  pageComponent: ComponentType
  isChromeless: boolean
  isModal: boolean
  basicDataRequirements: DataRequirements
  params: P
  nonav = false

  static isServerSideRender({ req }) {
    return !!req
  }
  constructor(opts: AsyncPageOptions) {
    this.title = opts.title
    this.pageComponent = opts.pageComponent
    this.isChromeless = !!opts.isChromeless
    this.basicDataRequirements =
      opts.basicDataRequirements || DEFAULT_DATA_REQUIREMENTS
  }

  /**
   * TODO: Dynamically import the component, allowing code-splitting so that clients don't
   * have to download script for the entire app in one shot. Currently there's a babel problem
   * that prevents the universal component being used here instead of in every single subclass :(
   */
  getComponent() {
    // if (typeof this.pageComponent === 'string'){
    //   return universal(props => import(this.pageComponent))
    // } else {
    return this.pageComponent
    // }
  }
  /**
   * TODO: Dynamically import extra navigation for display in the secondary navigation bar
   * see comment on getComponent ...
   */
  getExtraSecondaryNavigation(opt: any) {
    return undefined // override me to export extra navigation
  }
  updatePageHead(dispatch, getState, opts) {
    let data = this.createPageHeadInfo(getState, opts)
    if (data) {
      dispatch(updatePageHead(data))
    }
  }
  /**
   * Update the redux 'head' information used to render the html/head elements when
   * performing server-side-render. Currently has no effect on the head info on client-side.
   * Sub-classes should override to set page-specific head info.
   */
  createPageHeadInfo(getState, opts) {
    // example (override in sub-class):
    // let { instance: { value: instance } } = getState()
    // if (instance) {
    //   return {
    //     title: opts.title || instance.instanceName,
    //     keywords: opts.keywords || '',
    //     description: opts.description || ''
    //   }
    // }
  }
  /**
   * This is fired before the app attempts to render the page component.
   *
   * During server-side-render the promise returned here will be awaited before
   * attempting to render and return a result, so that SSR can return fully
   * populated pages.
   *
   * During client-side-render the promise returned here is not awaited, so pages
   * will render with incomplete data and must behave sensibly - e.g. showing
   * indeterminate progress indicators or "please wait" or whatever.
   */
  async prepareAsyncData(dispatch, getState, opts: { api: API, req }) {
    try {
      await this.prepareBasicAsyncData(dispatch, getState, opts)
      await this.prepareAdditionalAsyncData(dispatch, getState, opts)
      if (AsyncPage.isServerSideRender(opts)) {
        this.updatePageHead(dispatch, getState, opts)
      }
    } catch (err) {
      if (err.message === 'authentication_required') {
        let { location: { type, payload } } = getState()
        dispatch({
          type: 'home/welcome',
          payload: {
            destination: type,
            destination_payload: payload
          }
        })
      } else {
        // todo - show an error page?
      }
    }
  }
  /**
   * Most pages typically want the current user's profile and the basic
   * domain info, so these are prepared automatically unless a page
   * specifically opts-out by setting alternative basicDataRequirements
   */
  async prepareBasicAsyncData(dispatch, getState, opts) {
    if (
      this.basicDataRequirements &&
      Object.keys(this.basicDataRequirements).length
    ) {
      let state = getState()
      let { session: { value: profile }, instance: { value: instance } } = state
      let promises = []
      if (this.basicDataRequirements.instance && !instance) {
        promises.push(this.loadInstanceInfo(dispatch, getState, opts))
      }
      if ((this.basicDataRequirements.session && !profile) || this.basicDataRequirements.admin) {
        promises.push(this.loadProfile(dispatch, getState, opts))
      }
      await Promise.all(promises)
    }
  }
  async loadProfile(dispatch, getState, opts) {
    try {
      return await loadProfile(this.basicDataRequirements.admin)(dispatch, getState, opts)
    } catch (err) {
      log.error(err)
    }
  }
  async loadInstanceInfo(dispatch, getState, opts) {
    try {
      return await loadInstanceInfo()(dispatch, getState, opts)
    } catch (err) {
      log.error(err)
    }
  }
  /**
   * subclasses should override this if they want to prepare any
   * async data besides that prepared by prepareBasicAsyncData
   */
  async prepareAdditionalAsyncData(dispatch, getState, opts) {
    // no-op, override me if you need async data
  }

  getPageComponent = (place) => {
    let Component = this.getComponent(place)
    if (typeof Component === 'object') {
      return React.cloneElement(Component, {
        place: place.type,
        ...place.payload
      })
    } if (!place) {
      return <Component />
    } else {
      return <Component place={place.type} {...place.payload} />
    }
  }

  getKey = () => {
    if (!this.key) {
      this.key = String(count++)
    }
    return this.key
  }
}

export default AsyncPage
